import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { FileText, Plus, Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel, StatusBadge, formatDate } from "@/components/admin/ui";
import { centsToDisplay } from "@/lib/money";
import QuoteDrawer from "@/components/admin/QuoteDrawer";
import NewQuoteModal from "@/components/admin/NewQuoteModal";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

const STATUSES = ["Draft", "Sent", "Viewed", "Accepted", "Declined", "Expired"];

export default function AdminQuotes() {
  const { API } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [selectedId, setSelectedId] = useState(searchParams.get("focus") || null);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      const { data } = await axios.get(`${API}/admin/quotes`, { params, withCredentials: true });
      setQuotes(data.quotes || []);
      setStatusCounts(data.status_counts || {});
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, search, status]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  usePolling(() => load(true), 10000);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    if (selectedId) params.focus = selectedId;
    setSearchParams(params, { replace: true });
  }, [search, status, selectedId, setSearchParams]);

  const closeDrawer = () => setSelectedId(null);

  usePageLastUpdated(lastUpdated, "quotes-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-quotes-page">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setNewModalOpen(true)}
          data-testid="new-quote-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <Plus size={16} /> New Quote
        </button>
      </div>

      <Panel testId="quotes-table-panel">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="quote-status-tabs">
            {["All", ...STATUSES].map((item) => {
              const value = item === "All" ? "" : item;
              const active = status === value;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setStatus(value)}
                  data-testid={`quote-filter-${item.toLowerCase()}`}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${active ? "bg-[#0052FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"}`}
                >
                  {item}
                  {item !== "All" ? ` (${statusCounts[item] || 0})` : ""}
                </button>
              );
            })}
          </div>
          <label className="relative block">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="quote-search-input"
              placeholder="Search company, contact, quote ID…"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 sm:w-72 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
            />
          </label>
        </div>

        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="quotes-error">{error}</p>}

        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="quotes-loading">Loading quotes…</div>
          ) : quotes.length === 0 ? (
            <EmptyState icon={FileText} title="No quotes yet." message="Create a quote manually or from a Completed demo request." testId="quotes-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-left text-sm" data-testid="admin-quotes-list">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pr-4 font-bold">Quote ID</th>
                    <th className="py-3 pr-4 font-bold">Company</th>
                    <th className="py-3 pr-4 font-bold">Contact</th>
                    <th className="py-3 pr-4 font-bold">Initial Total</th>
                    <th className="py-3 pr-4 font-bold">Status</th>
                    <th className="py-3 pr-4 font-bold">Valid Until</th>
                    <th className="py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.quote_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`quote-row-${q.quote_id}`}>
                      <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{q.quote_id}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{q.company}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{q.contact_name}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{centsToDisplay(q.totals?.initial_total_cents)}</td>
                      <td className="py-4 pr-4"><StatusBadge status={q.effective_status} testId={`quote-row-status-${q.quote_id}`} /></td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDate(q.valid_until)}</td>
                      <td className="py-4">
                        <button
                          type="button"
                          onClick={() => setSelectedId(q.quote_id)}
                          data-testid={`quote-view-${q.quote_id}`}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      <QuoteDrawer quoteId={selectedId} open={!!selectedId} onClose={closeDrawer} onUpdated={load} />
      <NewQuoteModal
        open={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        onCreated={(quote) => {
          setNewModalOpen(false);
          load();
          setSelectedId(quote.quote_id);
        }}
      />
    </div>
  );
}
